import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { KeyRound, ShieldCheck, Store, UserCog, History, CreditCard, Calendar, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// ===== Billing helpers =====
function daysUntil(unix) {
  if (!unix) return null;
  const ms = unix * 1000 - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}
function fmtDate(unix) {
  if (!unix) return "—";
  return new Date(unix * 1000).toLocaleDateString("ja-JP", { year: "numeric", month: "numeric", day: "numeric" });
}
function statusLabel(s) {
  return {
    trialing: { label: "無料トライアル中", tone: "emerald", icon: Sparkles },
    active: { label: "ご契約中", tone: "blue", icon: CreditCard },
    past_due: { label: "支払い未完了", tone: "rose", icon: AlertTriangle },
    paused: { label: "一時停止中（支払方法未登録）", tone: "amber", icon: AlertTriangle },
    canceled: { label: "解約済み", tone: "slate", icon: CreditCard },
    unpaid: { label: "未払い", tone: "rose", icon: AlertTriangle },
    incomplete: { label: "登録手続き中", tone: "amber", icon: AlertTriangle },
    incomplete_expired: { label: "登録未完了で失効", tone: "slate", icon: AlertTriangle },
  }[s] || { label: s || "—", tone: "slate", icon: CreditCard };
}

/**
 * Manager/Staff 共通の「アカウント設定」画面。
 * - 自分のパスワード変更
 * - 自店舗の専用ログインURL表示 (manager のみ)
 */
export default function UserProfile() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [saving, setSaving] = useState(false);

  // 運営アクセス許可
  const [adminAccess, setAdminAccess] = useState({ admin_access_allowed: false, admin_access_allowed_at: null });
  const [adminLogs, setAdminLogs] = useState([]);
  const [pendingToggle, setPendingToggle] = useState(null); // null | true | false

  // 課金状態
  const [billing, setBilling] = useState(null); // null | { enabled, subscribed, status, trial_end, ... }
  const [openingPortal, setOpeningPortal] = useState(false);

  const tenantSlug = user?.tenant_slug;
  const loginUrl = tenantSlug ? `https://shiftmagic.jp/${tenantSlug}/login` : null;
  const isManager = user?.role === "manager";

  useEffect(() => {
    if (!isManager) return;
    api.get("/admin-access/me").then((r) => setAdminAccess(r.data)).catch(() => {});
    api.get("/admin-access/me/logs").then((r) => setAdminLogs(r.data)).catch(() => {});
    api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => setBilling({ enabled: false }));
  }, [isManager]);

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await api.post("/billing/portal");
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOpeningPortal(false);
    }
  };

  const confirmToggleAdminAccess = (next) => {
    setPendingToggle(next);
  };

  const doToggleAdminAccess = async () => {
    const next = pendingToggle;
    setPendingToggle(null);
    try {
      const { data } = await api.patch("/admin-access/me", { allowed: next });
      setAdminAccess((p) => ({ ...p, admin_access_allowed: data.admin_access_allowed, admin_access_allowed_at: data.admin_access_allowed ? new Date().toISOString() : null }));
      toast.success(data.admin_access_allowed ? "運営アクセスを許可しました" : "運営アクセスをブロックしました");
      const { data: logs } = await api.get("/admin-access/me/logs");
      setAdminLogs(logs);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changePw = async () => {
    if (next1.length < 6) {
      toast.error("新しいパスワードは6文字以上にしてください");
      return;
    }
    if (next1 !== next2) {
      toast.error("新しいパスワードの確認が一致しません");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next1 });
      toast.success("パスワードを変更しました");
      setCurrent(""); setNext1(""); setNext2("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="user-profile-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">アカウント設定</h1>
        <p className="text-sm text-slate-500 mt-1">パスワード変更ができます。</p>
      </div>

      <Card className="p-5 bg-white border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5 text-blue-600" />
          <h2 className="font-bold text-slate-900">基本情報</h2>
        </div>
        <div className="text-sm space-y-1 text-slate-700">
          <div><span className="text-slate-500 text-xs w-24 inline-block">ログインID</span> <code className="font-mono">{user?.login_id}</code></div>
          <div><span className="text-slate-500 text-xs w-24 inline-block">表示名</span> {user?.display_name || user?.name}</div>
          <div><span className="text-slate-500 text-xs w-24 inline-block">役割</span> {user?.role === "manager" ? "店長" : "スタッフ"}</div>
          {user?.company && <div><span className="text-slate-500 text-xs w-24 inline-block">店舗</span> {user.company}</div>}
        </div>
      </Card>

      {user?.role === "manager" && (
        <Card className="p-5 bg-blue-50 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Store className="w-5 h-5 text-blue-600" />
            <h2 className="font-bold text-blue-900">スタッフ用ログイン情報</h2>
          </div>
          <p className="text-xs text-blue-800 mb-3">
            スタッフには下記の3点をお伝えください。<br />
            <span className="text-[10px] text-blue-700">スタッフは <a href="/login" className="underline font-bold">{(typeof window !== "undefined" ? window.location.origin : "https://shiftmagic.jp")}/login</a> から「スタッフ」タブでログインできます。</span>
          </p>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="bg-white border border-blue-200 rounded p-2">
              <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">① 企業ログインID</p>
              <code className="block font-mono font-bold text-slate-900" data-testid="tenant-company-id">{user?.login_id}</code>
            </div>
            <div className="bg-white border border-blue-200 rounded p-2">
              <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">② スタッフのログインID</p>
              <p className="text-xs text-slate-600">スタッフ管理から各スタッフごとに設定 / 確認できます</p>
            </div>
            <div className="bg-white border border-blue-200 rounded p-2">
              <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">③ パスワード</p>
              <p className="text-xs text-slate-600">スタッフ管理の鍵アイコンから再発行できます</p>
            </div>
          </div>
          {loginUrl && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-[10px] text-blue-700 font-bold mb-1">参考: 店舗専用ログインURL（旧形式・併用可）</p>
              <code className="block bg-white border border-blue-200 rounded p-1.5 text-[11px] font-mono break-all text-slate-700" data-testid="tenant-login-url">{loginUrl}</code>
            </div>
          )}
        </Card>
      )}

      {/* ===== 課金状態 ===== */}
      {isManager && billing?.enabled && (
        <Card className="p-5 bg-white border border-slate-200" data-testid="billing-card">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-5 h-5 text-slate-700" />
            <h2 className="font-bold text-slate-900">ご契約・お支払い</h2>
          </div>
          {!billing.subscribed ? (
            <p className="text-sm text-slate-600">課金アカウントが未作成です。サポートまでお問い合わせください。</p>
          ) : (
            <>
              {(() => {
                const sl = statusLabel(billing.status);
                const Icon = sl.icon;
                const tone = {
                  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
                  blue: "bg-blue-50 border-blue-200 text-blue-800",
                  rose: "bg-rose-50 border-rose-200 text-rose-800",
                  amber: "bg-amber-50 border-amber-200 text-amber-800",
                  slate: "bg-slate-50 border-slate-200 text-slate-700",
                }[sl.tone];
                const days = daysUntil(billing.trial_end);
                return (
                  <div className={`rounded-lg border p-3 ${tone}`} data-testid="billing-status">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-bold">{sl.label}</span>
                    </div>
                    {billing.status === "trialing" && (
                      <p className="text-xs mt-2 leading-relaxed">
                        トライアル終了日：<b>{fmtDate(billing.trial_end)}</b>
                        {days != null && days > 0 && <> （残り <b>{days}</b> 日）</>}
                        <br />
                        ※ トライアル終了までに支払い方法をご登録いただくと、サービスを継続してご利用いただけます。
                      </p>
                    )}
                    {(billing.status === "active") && billing.current_period_end && (
                      <p className="text-xs mt-2 leading-relaxed">
                        次回更新日：<b>{fmtDate(billing.current_period_end)}</b>
                      </p>
                    )}
                    {(billing.status === "past_due" || billing.status === "unpaid") && (
                      <p className="text-xs mt-2 leading-relaxed">
                        お支払いが完了していません。下のボタンから支払い方法をご確認ください。
                      </p>
                    )}
                    {billing.status === "paused" && (
                      <p className="text-xs mt-2 leading-relaxed">
                        トライアルが終了しましたが、支払い方法が未登録のため一時停止中です。下のボタンから登録すると即時再開します。
                      </p>
                    )}
                  </div>
                );
              })()}
              <Button
                onClick={openPortal}
                disabled={openingPortal}
                data-testid="open-billing-portal"
                className="mt-3 bg-slate-900 hover:bg-slate-800 text-white"
              >
                <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                {openingPortal ? "ポータルを開いています..." : "支払い方法・請求情報の管理"}
              </Button>
              <p className="text-[10px] text-slate-400 mt-2">
                支払いはStripeで安全に処理されます。
              </p>
            </>
          )}
        </Card>
      )}

      {isManager && (
        <Card className="p-5 bg-white border border-slate-200" data-testid="admin-access-card">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-start gap-2">
              <UserCog className="w-5 h-5 text-slate-700 mt-0.5" />
              <div>
                <h2 className="font-bold text-slate-900">運営アクセス許可</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  ONにすると、運営（やさまるシフト サポート）があなたの管理画面に代理ログインし、初期設定やトラブル対応をサポートできます。OFFにすると、運営側からのログインは完全にブロックされます。
                </p>
              </div>
            </div>
            <div className="shrink-0 pt-1">
              <Switch
                data-testid="admin-access-toggle"
                checked={!!adminAccess.admin_access_allowed}
                onCheckedChange={(v) => confirmToggleAdminAccess(v)}
              />
            </div>
          </div>
          <div className={`rounded-lg px-3 py-2 text-xs font-bold flex items-center justify-between ${
            adminAccess.admin_access_allowed
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-slate-50 text-slate-700 border border-slate-200"
          }`}>
            <span data-testid="admin-access-status">
              {adminAccess.admin_access_allowed ? "🟢 運営アクセスを許可中" : "🔒 運営アクセスはブロック中"}
            </span>
            {adminAccess.admin_access_allowed && adminAccess.admin_access_allowed_at && (
              <span className="text-[10px] font-normal text-emerald-700">
                {new Date(adminAccess.admin_access_allowed_at).toLocaleString("ja-JP")} から有効
              </span>
            )}
          </div>

          {/* アクセス履歴 */}
          <div className="mt-4">
            <div className="flex items-center gap-1.5 mb-2">
              <History className="w-4 h-4 text-slate-500" />
              <h3 className="text-xs font-bold text-slate-700">運営アクセス履歴（直近100件）</h3>
            </div>
            {adminLogs.length === 0 ? (
              <p className="text-xs text-slate-400 italic px-2 py-3 bg-slate-50 rounded">
                履歴はまだありません
              </p>
            ) : (
              <ul className="space-y-1 max-h-64 overflow-y-auto pr-1" data-testid="admin-access-log-list">
                {adminLogs.map((l) => {
                  const label =
                    l.action === "impersonate_login"
                      ? `運営がログイン（${l.admin_login_id || "admin"}）`
                      : l.action === "toggle_on"
                        ? "あなたが許可をONにしました"
                        : "あなたが許可をOFFにしました";
                  const tone =
                    l.action === "impersonate_login"
                      ? "bg-amber-50 border-amber-200 text-amber-800"
                      : "bg-slate-50 border-slate-200 text-slate-600";
                  return (
                    <li key={l.id} className={`text-xs px-2.5 py-1.5 rounded border flex items-center justify-between ${tone}`}>
                      <span>{label}</span>
                      <span className="text-[10px] font-mono text-slate-500">
                        {new Date(l.created_at).toLocaleString("ja-JP")}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>
      )}

      <Card className="p-5 bg-white border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-5 h-5 text-blue-600" />
          <h2 className="font-bold text-slate-900">パスワード変更</h2>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold text-slate-700">現在のパスワード</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1" data-testid="current-pw" autoComplete="current-password" />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">新しいパスワード (6文字以上)</Label>
            <Input type="password" value={next1} onChange={(e) => setNext1(e.target.value)} className="mt-1" data-testid="new-pw-1" autoComplete="new-password" />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">新しいパスワード（確認）</Label>
            <Input type="password" value={next2} onChange={(e) => setNext2(e.target.value)} className="mt-1" data-testid="new-pw-2" autoComplete="new-password" />
          </div>
          <Button onClick={changePw} disabled={saving || !current || !next1 || !next2} className="bg-blue-600 hover:bg-blue-700" data-testid="change-pw-submit">
            {saving ? "変更中…" : "パスワードを変更"}
          </Button>
        </div>
      </Card>

      {/* 運営アクセス許可 切替の確認 */}
      <AlertDialog open={pendingToggle !== null} onOpenChange={(v) => !v && setPendingToggle(null)}>
        <AlertDialogContent data-testid="admin-access-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingToggle ? "運営アクセスを許可しますか？" : "運営アクセスをブロックしますか？"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingToggle
                ? "ONにしている間、運営（やさまるシフト サポート）があなたの管理画面に代理ログインできるようになります。サポートが終わったら、必ずOFFに戻してください。"
                : "OFFにすると、運営側からはあなたの管理画面に一切ログインできなくなります。サポートが必要なときは、再度ONにしてください。"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-access-cancel">キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={doToggleAdminAccess}
              className={pendingToggle ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 hover:bg-slate-800"}
              data-testid="admin-access-confirm-ok"
            >
              {pendingToggle ? "許可する" : "ブロックする"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
